import { useLayoutEffect, useRef, useState } from "react";

type FieldValues = Readonly<Record<string, string>>;
type FieldErrors = Readonly<Record<string, string | undefined>>;

/** Read usable field messages only from the server validation envelope. */
function validationFields(error: unknown): Record<string, string> {
  const envelope = (
    error as {
      envelope?: { code?: unknown; details?: { fieldErrors?: unknown } };
    } | null
  )?.envelope;
  const fields = envelope?.details?.fieldErrors;
  if (
    envelope?.code !== "VALIDATION_FAILED" ||
    !fields ||
    typeof fields !== "object" ||
    Array.isArray(fields)
  )
    return {};
  return Object.fromEntries(
    Object.entries(fields).flatMap(([field, messages]) => {
      if (!Array.isArray(messages)) return [];
      const text = messages
        .filter(
          (message): message is string =>
            typeof message === "string" && message.trim() !== "",
        )
        .join(" ");
      return text ? [[field, text]] : [];
    }),
  );
}

/** KPI DTO paths belong to the values the server refused. An edit retires an
 * error even if the user later types the refused value again. A record switch
 * also retires pending responses, including a switch away and back. */
export function useKpiFieldErrors(scope: string, values: FieldValues) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const committed = useRef({
    scope,
    generation: 0,
    values,
    revisions: {} as Record<string, number>,
  });
  const requestId = useRef(0);

  // Commit the latest controls before an asynchronous mutation can settle.
  useLayoutEffect(() => {
    const previous = committed.current;
    if (previous.scope !== scope) {
      committed.current = {
        scope,
        generation: previous.generation + 1,
        values,
        revisions: {},
      };
      setErrors({});
      return;
    }
    const changed = Object.keys(values).filter(
      (field) => previous.values[field] !== values[field],
    );
    if (changed.length === 0) return;
    for (const field of changed)
      previous.revisions[field] = (previous.revisions[field] ?? 0) + 1;
    previous.values = values;
    setErrors((current) => {
      if (!changed.some((field) => Object.hasOwn(current, field)))
        return current;
      return Object.fromEntries(
        Object.entries(current).filter(([field]) => !changed.includes(field)),
      );
    });
  });

  /** Capture the field revisions and record generation for this submission. */
  function beginRequest() {
    const generation = committed.current.generation;
    const revisions = { ...committed.current.revisions };
    const id = ++requestId.current;
    setErrors({});
    const isCurrent = () =>
      committed.current.generation === generation && requestId.current === id;
    return {
      isCurrent,
      reject(error: unknown): boolean {
        if (!isCurrent()) return false;
        setErrors(
          Object.fromEntries(
            Object.entries(validationFields(error)).filter(
              ([field]) =>
                Object.hasOwn(values, field) &&
                (committed.current.revisions[field] ?? 0) ===
                  (revisions[field] ?? 0),
            ),
          ),
        );
        return true;
      },
    };
  }

  return { errors, beginRequest };
}
